/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';

export class HeatmapDataPoint {
    location: google.maps.LatLng;
    weight: number;
    assetId: string;

    constructor(location: google.maps.LatLng, weight: number, assetId: string) {
        this.location = location;
        this.weight = weight;
        this.assetId = assetId;
    }
}

export class VisionHeatmap {
    public allPoints: HeatmapDataPoint[] = [];
    private pointsByAsset: Map<string, HeatmapDataPoint[]> = new Map<string, HeatmapDataPoint[]>();
    private visibleAssets: Set<string> = new Set<string>();

    public deckOverlay!: GoogleMapsOverlay;
    public heatmapVisible = true;

    public heatData: { location: google.maps.LatLng; weight: number; asset: string }[] = [];

    public heatmapLayer!: HeatmapLayer;

    constructor(map: google.maps.Map) {
        this.deckOverlay = new GoogleMapsOverlay({
            layers: [],
        });
        this.deckOverlay.setMap(map);
    }

    public indexPoints() {
        this.pointsByAsset.clear();

        for (const p of this.allPoints) {
            if (!this.pointsByAsset.has(p.assetId)) {
                this.pointsByAsset.set(p.assetId, []);
            }
            this.pointsByAsset.get(p.assetId)!.push(p);
        }
    }

    public toggleAsset(assetId: string, state: boolean) {
        if (this.visibleAssets.has(assetId) && state == false) {
            this.visibleAssets.delete(assetId);
        } else if (state == true) {
            this.visibleAssets.add(assetId);
        }

        this.createHeatmapLayer();
        this.setProps();
    }

    public setVisibleAssets(assetIds: string[]) {
        this.visibleAssets = new Set(assetIds);
    }

    public clearHeatmap() {
        this.allPoints = [];
        this.heatData = [];
        this.pointsByAsset.clear();
        this.clearVisible();
        this.setHeatmapData([]);
        this.setHeatmapVisible(false);
    }

    /**
     * Clear all visible assets
     */
    public clearVisible() {
        this.visibleAssets.clear();
    }

    /**
     * Get currently visible assets
     */
    public getVisibleAssets(): string[] {
        return Array.from(this.visibleAssets);
    }

    private buildFilteredData(): HeatmapDataPoint[] {
        const result: HeatmapDataPoint[] = [];

        for (const assetId of this.visibleAssets) {
            const pts = this.pointsByAsset.get(assetId);
            if (pts) result.push(...pts);
        }

        return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public setHeatmapData(heatData: any[], densify = false) {
        this.heatData = heatData;
        if (densify) {
            this.heatData = this.densify(heatData);
        }
    }

    public createHeatmapLayer() {
        const filteredData = this.buildFilteredData();

        this.heatmapLayer = new HeatmapLayer({
            id: 'grading-heatmap',
            data: filteredData,
            getPosition: (d) => [d.location.lng(), d.location.lat()],
            getWeight: (d) => d.weight ?? 1,
            radiusPixels: 40,
            intensity: 1,
            opacity: 0.5,
            pickable: false,
            aggregation: 'SUM',
        });
        return this.heatmapLayer;
    }

    public setProps() {
        this.deckOverlay.setProps({
            layers: [this.heatmapLayer],
        });
    }

    public setHeatmapVisible(visible: boolean) {
        this.heatmapVisible = visible;

        this.deckOverlay.setProps({
            layers: visible ? [this.createHeatmapLayer()] : [],
        });
    }

    public densify(path: { location: google.maps.LatLng; weight: number; asset: string }[], steps = 25) {
        const pts: { location: google.maps.LatLng; weight: number; asset: string }[] = [];
        for (let i = 0; i < path.length - 1; i++) {
            const a = path[i];
            const b = path[i + 1];
            for (let t = 0; t <= 1; t += 1 / steps) {
                const lng = a.location.lng() + t * (b.location.lng() - a.location.lng());
                const lat = a.location.lat() + t * (b.location.lat() - a.location.lat());
                pts.push({ location: new google.maps.LatLng(lat, lng), weight: 1, asset: a.asset });
            }
        }
        return pts;
    }
}
